import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react'
import {
  SEARCH_GOOGLE_PLACES,
  SET_CLIENT_PLACE_ID,
  GET_CLIENT_GBP_PLACE,
  REMOVE_CLIENT_PLACE_ID,
  GET_GBP_REVIEWS,
} from '@/lib/operations/dashboard'
import type { GbpPlaceResult } from '@/lib/types'

export function useSearchGooglePlaces() {
  const [search, { data, loading, error, called }] = useLazyQuery<{
    searchGooglePlaces: GbpPlaceResult[]
  }>(SEARCH_GOOGLE_PLACES, { fetchPolicy: 'network-only' })

  const searchPlaces = (clientId: string, query: string) =>
    search({ variables: { clientId, query } })

  return {
    searchPlaces,
    results: data?.searchGooglePlaces ?? [],
    loading,
    error,
    called,
  }
}

export function useSetClientPlaceId() {
  const [mutate, { loading, data, error }] = useMutation<{ setClientPlaceId: boolean }>(
    SET_CLIENT_PLACE_ID
  )

  const setPlaceId = (clientId: string, placeId: string) =>
    mutate({
      variables: { clientId, placeId },
      refetchQueries: [{ query: GET_CLIENT_GBP_PLACE, variables: { clientId } }],
    })

  return { setPlaceId, loading, success: data?.setClientPlaceId ?? false, error }
}

export function useClientGbpPlace(clientId: string | null) {
  const { data, loading, error } = useQuery<{ clientGbpPlace: GbpPlaceResult | null }>(
    GET_CLIENT_GBP_PLACE,
    {
      variables: { clientId },
      skip: !clientId,
    }
  )
  return { place: data?.clientGbpPlace ?? null, loading, error }
}

export function useRemoveClientPlaceId() {
  const [mutate, { loading, error }] = useMutation<{ removeClientPlaceId: boolean }>(
    REMOVE_CLIENT_PLACE_ID
  )

  const removePlaceId = (clientId: string) =>
    mutate({
      variables: { clientId },
      refetchQueries: [
        { query: GET_CLIENT_GBP_PLACE, variables: { clientId } },
        { query: GET_GBP_REVIEWS, variables: { clientId, locationId: null } },
      ],
    })

  return { removePlaceId, loading, error }
}
